<template>
    <article class="oi">
        <button type="button" class="oi-thumb" :class="{'is-loading': imageLoading, 'is-empty': !imageLoading && !imageUrl}"
            :disabled="!imageUrl" :title="imageUrl ? 'Ver imagem' : ''" @click="$emit('view-image', imageUrl)">
            <img v-if="imageUrl" :src="imageUrl" :alt="item.product.name" />
            <fa v-else-if="!imageLoading" icon="box" />
        </button>

        <div class="oi-main">
            <p class="oi-name">{{item.product.name || 'Produto sem nome'}}</p>

            <p class="oi-variations" v-if="variations.length">
                <span v-for="v in variations" :key="v.name" class="oi-variation"><small>{{v.name}}</small>{{v.value}}</span>
            </p>

            <p class="oi-codes">
                <span v-if="item.product.skuCode">
                    <small>SKU</small><code>{{item.product.skuCode}}</code>
                    <CopyButtonCmp class="oi-copy" :value="item.product.skuCode" label="SKU" />
                </span>
                <span v-if="item.product.codeSource">
                    <small>Retaguarda</small><code>{{item.product.codeSource}}</code>
                    <CopyButtonCmp class="oi-copy" :value="item.product.codeSource" label="código da retaguarda" />
                </span>
            </p>

            <p class="oi-links" v-if="item.product.id">
                <a :href="productUrl" target="_blank" rel="noopener">
                    Ver produto <fa icon="external-link-alt" />
                </a>
                <a :href="ordersUrl" target="_blank" rel="noopener">
                    Pedidos com este produto <fa icon="external-link-alt" />
                </a>
            </p>
        </div>

        <dl class="oi-values">
            <div>
                <dt>Qtd x preço</dt>
                <dd>{{item.amount}} x {{item.price | currency}}</dd>
            </div>
            <div v-if="item.getDiscountValue() > 0">
                <dt>Desconto</dt>
                <dd class="is-discount">- {{item.getDiscountValue() | currency}}</dd>
            </div>
            <div class="is-total">
                <dt>Total</dt>
                <dd>{{item.getTotal() | currency}}</dd>
            </div>
        </dl>
    </article>
</template>

<script>
import { OrderItemModel } from '@/order/models/OrderItemModel'
import CopyButtonCmp from '@/utils/components/CopyButtonCmp.vue'

/** Um produto comprado no pedido: imagem, identificação e atalhos para o produto e seus pedidos. */
export default {
    name: 'OrderItemCardCmp',
    components: {
        CopyButtonCmp,
    },
    props: {
        item: {
            type: OrderItemModel,
            required: true,
        },
        imageUrl: {
            type: String,
            default: '',
        },
        imageLoading: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        variations() {
            const product = this.item.product

            if (!product.isSku || !product.getAttributeTypeGridList) {
                return []
            }

            return product.getAttributeTypeGridList().map((a) => ({name: a.attribute ? a.attribute.name : '', value: a.value}))
        },
        productUrl() {
            return `/produtos/${this.item.product.id}`
        },
        ordersUrl() {
            return `/pedidos?custom=true&productIdList=${this.item.product.id}`
        },
    },
}
</script>

<style lang="scss" scoped>
$mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
$ease-out: cubic-bezier(.22, 1, .36, 1);

.oi {
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr) auto;
    align-items: start;
    gap: 14px 16px;
    padding: 14px 0;

    & + & {
        border-top: 1px solid var(--border-subtle);
    }

    @media (max-width: 767px) {
        grid-template-columns: 64px minmax(0, 1fr);

        .oi-values {
            grid-column: 1 / -1;
        }
    }
}

.oi-thumb {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    padding: 0;
    overflow: hidden;
    border: 1px solid var(--border-subtle);
    border-radius: 14px;
    color: var(--text-faint);
    background: var(--surface-2);
    font-size: 22px;
    cursor: zoom-in;
    transition: transform .2s $ease-out, box-shadow .2s;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    &:hover:not(:disabled) {
        box-shadow: var(--shadow-md);
        transform: translateY(-1px);
    }

    &:disabled {
        cursor: default;
    }

    &.is-loading {
        background: linear-gradient(90deg, var(--surface-3) 0%, var(--surface-2) 50%, var(--surface-3) 100%);
        background-size: 200% 100%;
        animation: oi-shimmer 1.2s linear infinite;
    }

    @media (max-width: 767px) {
        width: 64px;
        height: 64px;
    }
}

.oi-main {
    min-width: 0;

    p {
        margin: 0;
    }
}

.oi-name {
    color: var(--text-strong);
    font-size: 15px;
    font-weight: 700;
    line-height: 1.35;
}

.oi-variations {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 6px !important;
}

.oi-variation {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
    padding: 1px 9px;
    border: 1px solid var(--tint-brand-border);
    border-radius: 999px;
    color: var(--brand-strong);
    background: var(--tint-brand-soft);
    font-size: 12px;
    font-weight: 700;

    small {
        color: var(--text-faint);
        font-size: 10px;
        letter-spacing: .06em;
        text-transform: uppercase;
    }
}

.oi-codes {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    margin-top: 6px !important;

    span {
        display: inline-flex;
        align-items: center;
        gap: 5px;
    }

    .oi-copy {
        min-width: 22px;
        height: 22px;
        font-size: 10px;
    }

    small {
        color: var(--text-faint);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: .08em;
        text-transform: uppercase;
    }

    code {
        color: var(--text-soft);
        font-family: $mono;
        font-size: 12px;
    }
}

.oi-links {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    margin-top: 8px !important;

    a {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: var(--brand);
        font-size: 13px;
        font-weight: 700;

        svg {
            font-size: 10px;
            opacity: .8;
        }
    }
}

.oi-values {
    display: grid;
    gap: 4px;
    min-width: 150px;
    margin: 0;
    text-align: right;

    div {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 12px;
    }

    dt {
        color: var(--text-faint);
        font-size: 11px;
        font-weight: 700;
        letter-spacing: .06em;
        text-transform: uppercase;
    }

    dd {
        margin: 0;
        color: var(--text);
        font-size: 13px;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;

        &.is-discount {
            color: var(--success-fg);
        }
    }

    .is-total dd {
        color: var(--text-strong);
        font-size: 15px;
        font-weight: 800;
    }

    @media (max-width: 767px) {
        padding-top: 10px;
        border-top: 1px dashed var(--border-subtle);
    }
}

@keyframes oi-shimmer {
    to {
        background-position: -200% 0;
    }
}
</style>
